import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Edit, Trash2, X, Calendar, Package, User, FileCheck, AlertTriangle, CheckCircle, Clock, History, XCircle, MessageSquare, Printer, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { decryptId, encryptId } from '../utils/urlEncryption';
import SideMenu from '../components/SideMenu';
import RevisionTimelineCard from '../components/RevisionTimelineCard';
import PrintFormatSelector from '../components/PrintFormatSelector';
import ApprovalPrintA4Customer from '../components/ApprovalPrintA4Customer';
import ApprovalPrintA4Internal from '../components/ApprovalPrintA4Internal';
import ApprovalPrintThermalCustomer from '../components/ApprovalPrintThermalCustomer';
import ApprovalPrintThermalInternal from '../components/ApprovalPrintThermalInternal';
import ItemReceiptPrint from '../components/ItemReceiptPrint';
import ItemReceiptSelector from '../components/ItemReceiptSelector';
import { openPrintWindow, getElementOuterHTML } from '../utils/printWindow';
import '../styles/print.css';

const ApprovalDetail = ({ user, onLogout }) => {
  const { approvalId: encryptedApprovalId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPage = searchParams.get('from'); // 'pending' or 'provisional'
  
  // Decrypt the approval ID from URL
  const approvalId = decryptId(encryptedApprovalId);
  
  const [approval, setApproval] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reminder, setReminder] = useState(null);
  const [expandedPaymentMode, setExpandedPaymentMode] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printFormat, setPrintFormat] = useState(null);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [showItemReceiptSelector, setShowItemReceiptSelector] = useState(false);

  useEffect(() => {
    loadApproval();
  }, [approvalId]);

  const loadApproval = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/approvals/${approvalId}`);
      setApproval(res.data);
      
      // Load customer details
      if (res.data.customer_id) {
        try {
          const custRes = await axiosInstance.get(`/customers/${res.data.customer_id}`);
          setCustomer(custRes.data);
        } catch (err) {
          console.error('Error loading customer:', err);
        }
      }
      
      // Load reminder for this approval
      try {
        const reminderRes = await axiosInstance.get(`/reminders`, {
          params: { approval_id: approvalId }
        });
        if (reminderRes.data && reminderRes.data.length > 0) {
          setReminder(reminderRes.data[0]);
        }
      } catch (reminderErr) {
        // Reminder not found is okay
        console.log('No reminder found');
      }

      // Load terms and conditions
      try {
        const termsRes = await axiosInstance.get('/terms-and-conditions');
        if (termsRes.data && termsRes.data.content) {
          setTermsAndConditions(termsRes.data.content);
        }
      } catch (termsErr) {
        console.log('No terms and conditions found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading approval');
      navigate('/approvals/reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/approvals/${approvalId}`);
      toast.success('Approval deleted successfully');
      navigate('/approvals/reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting approval');
    }
  };

  // Admin approval functions - for pending_approval status
  const handleApprove = async () => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/approve`);
      toast.success('Approval approved successfully!');
      loadApproval();
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await axiosInstance.post(`/approvals/${approvalId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Approval rejected');
      setShowRejectModal(false);
      navigate('/admin/approvals');
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Failed to reject');
    }
  };

  // Admin functions for provisional changes
  const handleApproveChanges = async () => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/approve-changes`);
      toast.success('Provisional changes approved!');
      loadApproval();
    } catch (error) {
      console.error('Error approving changes:', error);
      toast.error('Failed to approve changes');
    }
  };

  const handleRejectChanges = async () => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/reject-changes`);
      toast.success('Changes rejected and rolled back');
      loadApproval();
    } catch (error) {
      console.error('Error rejecting changes:', error);
      toast.error('Failed to reject changes');
    }
  };
  
  const shareOnWhatsApp = () => {
    if (!customer) {
      toast.error('Customer information not available');
      return;
    }
    
    const phone = customer.mobile || customer.phone;
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    
    const totals = calculateTotals();
    
    // Helper functions
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString('en-IN', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    };

    // Group payments by mode
    const paymentsByMode = {};
    (approval.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!paymentsByMode[mode]) paymentsByMode[mode] = [];
      paymentsByMode[mode].push(payment);
    });
    
    // Generate detailed message matching A4 customer print format
    let message = '';
    
    // Header
    message += '═══════════════════════════════════\n';
    message += '       APPROVAL DETAILS\n';
    message += '═══════════════════════════════════\n';
    message += `Approval ID: ${approval.approval_id}\n`;
    message += `Date: ${formatDate(approval.approval_date || approval.created_at)}\n`;
    message += `Status: ${approval.status}\n\n`;
    
    // Customer Info
    message += '───────────────────────────────────\n';
    message += 'CUSTOMER INFORMATION\n';
    message += '───────────────────────────────────\n';
    message += `Name: ${customer.name}\n`;
    if (customer.mobile) message += `Mobile: ${customer.mobile}\n`;
    if (customer.alternate_mobile) message += `Alt Mobile: ${customer.alternate_mobile}\n`;
    if (customer.email) message += `Email: ${customer.email}\n`;
    if (customer.address) message += `Address: ${customer.address}\n`;
    message += '\n';
    
    // Rate Info
    if (approval.base_24k_rate) {
      message += '───────────────────────────────────\n';
      message += 'RATE INFORMATION\n';
      message += '───────────────────────────────────\n';
      message += `24K Rate: ₹{formatCurrency(order.base24k_rate_ex_gst)}/g\n`;
      if (approval.gst_applicable === false) {
        message += `GST: Kaccha (No GST)\n`;
      } else {
        message += `GST: ${approval.gst_percent || 3}% (${approval.gst_input_style === 'INCL' ? 'Inclusive' : 'Exclusive'})\n`;
      }
      message += '\n';
    }
    
    // Items
    if (approval.items && approval.items.length > 0) {
      message += '───────────────────────────────────\n';
      message += `ITEMS (${approval.items.length})\n`;
      message += '───────────────────────────────────\n\n';
      
      approval.items.forEach((item, idx) => {
        message += `${idx + 1}. ${item.item_type}\n`;
        message += '───────────────────────────────────\n';
        
        // Item Photos with Links
        if (item.photos && item.photos.length > 0) {
          item.photos.forEach((photo, photoIdx) => {
            message += `📷 Image ${photoIdx + 1}: ${photo}\n`;
          });
          message += '\n';
        }
        
        // Item Details
        if (item.tag_number) message += `Tag: ${item.tag_number}\n`;
        
        if (item.gross_wt) {
          message += `Gross Wt: ${item.gross_wt}g\n`;
          if (item.less_wt) message += `Less Wt: ${item.less_wt}g\n`;
          message += `Net Wt: ${item.net_wt || item.gross_wt}g\n`;
        } else if (item.weight_type === 'Fixed' && item.weight_fixed) {
          message += `Weight: ${item.weight_fixed}g\n`;
        } else if (item.weight_type === 'Range') {
          message += `Weight: ${item.weight_min}-${item.weight_max}g\n`;
        }
        
        if (item.purity) message += `Purity: ${item.purity}\n`;
        if (item.quantity) message += `Quantity: ${item.quantity}\n`;
        if (item.size) message += `Size: ${item.size}\n`;
        if (item.public_remark) message += `Remark: ${item.public_remark}\n`;
        message += '\n';
      });
    }
    
    // Summary
    message += '═══════════════════════════════════\n';
    message += 'SUMMARY\n';
    message += '═══════════════════════════════════\n';
    message += `Total Items: ${approval.items?.length || 0}\n`;
    message += `Gross Weight: ${totals.totalGrossWeight.toFixed(2)}g\n`;
    message += `Net Weight: ${totals.totalNetWeight.toFixed(2)}g\n`;
    message += `Fine Weight: ${totals.totalFineWeight.toFixed(2)}g\n`;
    if (approval.base_24k_rate) {
      message += `Estimated Value: ₹${formatCurrency(totals.estimatedValue)}\n`;
    }
    message += '\n';
    
    // Payments
    if (approval.advance_payments && approval.advance_payments.length > 0) {
      message += '───────────────────────────────────\n';
      message += 'PAYMENTS\n';
      message += '───────────────────────────────────\n\n';
      
      Object.keys(paymentsByMode).forEach(mode => {
        message += `${mode.toUpperCase()}\n`;
        message += '───────────────────────────────────\n';
        
        paymentsByMode[mode].forEach(payment => {
          const amount = parseFloat(payment.amount) || 0;
          const isRefund = payment.transaction_type === 'REFUND';
          message += `${formatDate(payment.date)}: ${isRefund ? '-' : ''}₹${formatCurrency(Math.abs(amount))}`;
          if (isRefund) message += ' (REFUND)';
          message += '\n';
        });
        
        const modeTotal = paymentsByMode[mode].reduce((sum, p) => {
          const amount = parseFloat(p.amount) || 0;
          return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
        }, 0);
        message += `Total: ₹${formatCurrency(modeTotal)}\n\n`;
      });
      
      message += `TOTAL ADVANCE PAID: ₹${formatCurrency(totals.totalAdvance)}\n`;
      message += '═══════════════════════════════════\n\n';
    }
    
    message += 'Thank you for choosing us!\n';
    
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/91${cleanPhone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  // Extract Old Gold and Metal Credit items from payments
  const getOldGoldAndMetalCreditItems = () => {
    const payments = approval?.advance_payments || approval?.payments || [];
    
    const items = [];
    
    payments.forEach((payment) => {
      // Old Gold items
      if (payment.mode === 'Old Gold') {
        if (payment.items && Array.isArray(payment.items) && payment.items.length > 0) {
          payment.items.forEach((item) => {
            items.push({
              ...item,
              gross_wt: item.gross_wt || item.grossWt,
              net_wt: item.net_wt || item.netWt,
              less_wt: item.less_wt || item.lessWt,
              fine_wt: item.fine_wt || item.fineWt,
              purity: item.purity,
              rate: item.purchase_rate || item.rate,
              value: item.line_total || item.value,
              description: item.item_name || item.description,
              payment_id: payment.payment_id,
              payment_date: payment.date,
              type: 'old_gold'
            });
          });
        } else {
          items.push({
            gross_wt: payment.gross_wt,
            net_wt: payment.net_wt,
            stone_wt: payment.stone_wt,
            less_wt: payment.less_wt,
            purity: payment.purity,
            fine_wt: payment.fine_wt || payment.fine_grams,
            rate: payment.rate_per_gram,
            value: payment.total_value,
            payment_id: payment.payment_id,
            payment_date: payment.date,
            type: 'old_gold'
          });
        }
      }
      
      // Metal Credit items
      if (payment.mode === 'Metal Credit' && payment.deposits && Array.isArray(payment.deposits)) {
        payment.deposits.forEach((deposit) => {
          items.push({
            ...deposit,
            deposits: [deposit],
            payment_id: payment.payment_id,
            payment_date: payment.date,
            type: 'metal_credit'
          });
        });
      }
    });
    
    return items;
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const handlePrintFormat = (format) => {
    setShowPrintModal(false);
    
    if (format === 'item-receipts') {
      setShowItemReceiptSelector(true);
      return;
    }
    
    // Set the format to render the print component in the DOM
    setPrintFormat(format);
  };

  // Once printFormat is set and component renders, grab its HTML and open print tab
  useEffect(() => {
    if (!printFormat || printFormat === 'item-receipts') return;
    
    // Small delay to ensure the print component has rendered
    const timer = setTimeout(() => {
      let content = '';
      let title = '';
      let pageSize = 'A4';
      
      if (printFormat === 'customer') {
        content = getElementOuterHTML('approval-a4-customer-print-content');
        title = `Approval ${approval?.approval_id} - Customer Copy`;
      } else if (printFormat === 'internal') {
        content = getElementOuterHTML('approval-a4-internal-print-content');
        title = `Approval ${approval?.approval_id} - Internal Copy`;
      } else if (printFormat === 'thermal-customer') {
        content = getElementOuterHTML('approval-thermal-customer-print-content');
        title = `Approval ${approval?.approval_id} - Customer Thermal`;
        pageSize = 'thermal';
      } else if (printFormat === 'thermal-internal') {
        content = getElementOuterHTML('approval-thermal-internal-print-content');
        title = `Approval ${approval?.approval_id} - Internal Thermal`;
        pageSize = 'thermal';
      }
      
      if (content) {
        openPrintWindow(content, title, { pageSize });
      } else {
        toast.error('Print content not found. Please try again.');
      }
      
      // Reset so the component unmounts
      setPrintFormat(null);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [printFormat]);

  const handlePrintSelectedItems = (selectedIndices) => {
    const items = getOldGoldAndMetalCreditItems();
    setShowItemReceiptSelector(false);
    
    if (selectedIndices.length === 0) {
      toast.error('Please select at least one receipt to print');
      return;
    }
    
    // Build HTML for selected receipts
    let receiptsHTML = '';
    selectedIndices.forEach((index) => {
      const receiptElement = document.getElementById(`item-receipt-${index}`);
      if (receiptElement) {
        receiptsHTML += receiptElement.outerHTML;
      }
    });
    
    if (!receiptsHTML) {
      toast.error('Selected receipts not found');
      return;
    }
    
    // Open print window with selected receipts
    openPrintWindow(
      receiptsHTML,
      `Approval ${approval?.approval_id} - Item Receipts`,
      { pageSize: 'thermal' }
    );
  };

  const handleReceiveBack = async () => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/receive-back`);
      toast.success('Approval marked as Returned/Received Back');
      loadApproval();
    } catch (error) {
      console.error('Error receiving back approval:', error);
      toast.error('Failed to update status');
    }
  };

  const handleRevertToActive = async () => {
    try {
      await axiosInstance.post(`/approvals/${approvalId}/revert-to-active`);
      toast.success('Approval status reverted to Active');
      loadApproval();
    } catch (error) {
      console.error('Error reverting approval:', error);
      toast.error('Failed to revert status');
    }
  };
  
  const handleEdit = () => {
    navigate(`/approvals/edit/${encryptId(approvalId)}`);
  };
  
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN')}`;
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!approval) return { totalGrossWeight: 0, totalNetWeight: 0, totalFineWeight: 0, totalAdvance: 0, estimatedValue: 0 };
    
    const items = approval.items || [];
    // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
    const totalGrossWeight = items.reduce((sum, item) => {
      if (item.gross_wt) return sum + parseFloat(item.gross_wt);
      return sum + (parseFloat(item.weight_fixed) || parseFloat(item.weight) || 0);
    }, 0);
    const totalNetWeight = items.reduce((sum, item) => {
      if (item.net_wt) return sum + parseFloat(item.net_wt);
      if (item.gross_wt) return sum + parseFloat(item.gross_wt);
      return sum + (parseFloat(item.weight_fixed) || parseFloat(item.weight) || 0);
    }, 0);
    const totalFineWeight = items.reduce((sum, item) => {
      let netWt = 0;
      if (item.net_wt) netWt = parseFloat(item.net_wt);
      else if (item.gross_wt) netWt = parseFloat(item.gross_wt);
      else netWt = parseFloat(item.weight_fixed) || parseFloat(item.weight) || 0;
      const purity = parseFloat(item.purity) || 100;
      return sum + (netWt * purity / 100);
    }, 0);
    
    const payments = approval.advance_payments || [];
    const totalAdvance = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    
    let estimatedValue = 0;
    if (approval.base_24k_rate) {
      estimatedValue = totalFineWeight * parseFloat(approval.base_24k_rate);
      if (approval.gst_applicable && approval.gst_input_style === 'EXCL') {
        estimatedValue *= (1 + (approval.gst_percent || 3) / 100);
      }
    }
    
    return { totalGrossWeight, totalNetWeight, totalFineWeight, totalAdvance, estimatedValue };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <p className="text-gray-600">Approval not found</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20 ${approval?.has_provisional_changes ? 'print-watermark' : ''}`}>
      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
      
      {/* Header with Actions - Matching Order Detail Style */}
      <div className="bg-white border-b-2 border-amber-200 shadow-sm print:hidden">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Back Button and Approval ID */}
          <div className="flex items-center gap-2 mb-3">
            <button 
              onClick={() => fromPage ? navigate('/admin-approvals') : navigate('/approvals/reports')} 
              className="p-2 hover:bg-gray-100 rounded-lg touch-target flex-shrink-0"
              title={fromPage ? 'Back to Pending Approvals' : 'Back to Approvals'}
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate">{approval.approval_id}</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {new Date(approval.created_at).toLocaleDateString('en-IN')}
              </p>
            </div>
          </div>
          
          {/* Action Buttons - Optimized for Mobile */}
          <div className="space-y-2">
            {/* Admin Action Buttons - Show when coming from pending/provisional */}
            {fromPage && user?.role === 'Admin' && (
              <div className="flex gap-2">
                <button
                  onClick={handleApprove}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold text-sm touch-target"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Approve {fromPage === 'provisional' ? 'Changes' : ''}</span>
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold text-sm touch-target"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Reject</span>
                </button>
              </div>
            )}
            
            {/* Regular Action Buttons - Grid Layout for Mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                onClick={shareOnWhatsApp}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-100 text-green-700 rounded-lg font-semibold hover:bg-green-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Share</span>
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <Printer className="w-4 h-4" />
                <span>Print</span>
              </button>
              <button
                onClick={handleEdit}
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-100 text-amber-700 rounded-lg font-semibold hover:bg-amber-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
              {(user?.role === 'Admin' || user?.role === 'Manager') && (
                <button
                  onClick={handleDeleteClick}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-xs sm:text-sm touch-target"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
            
            {/* Receive Back Button - Show only when status is Active */}
            {approval.status === 'Active' && (
              <button
                onClick={handleReceiveBack}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-colors text-sm touch-target"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Receive Back</span>
              </button>
            )}

            {/* Revert to Active Button - Show only when status is Returned */}
            {approval.status === 'Returned' && (
              <button
                onClick={handleRevertToActive}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-orange-100 text-orange-700 rounded-lg font-semibold hover:bg-orange-200 transition-colors text-sm touch-target"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Revert to Active</span>
              </button>
            )}
            
            {/* Status badges */}
            <div className="flex gap-2 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                approval.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                approval.approval_status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
                approval.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {approval.approval_status === 'pending_approval' ? 'Pending Admin' : 
                 approval.approval_status === 'approved' ? 'Approved' :
                 approval.approval_status === 'rejected' ? 'Rejected' : approval.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                approval.status === 'Active' ? 'bg-blue-100 text-blue-700' :
                approval.status === 'Returned' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {approval.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Provisional Changes Alert */}
      {approval.has_provisional_changes && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <div className="bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-400 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-orange-800 mb-1">Provisional Changes - Pending Admin Review</h3>
                <p className="text-sm text-orange-700">
                  This approval was modified by <span className="font-semibold">{approval.provisional_by}</span> on{' '}
                  {new Date(approval.provisional_since).toLocaleString('en-IN')}. Changes are visible but awaiting approval.
                </p>
                {user?.role === 'Admin' && (
                  <div className="flex gap-3 mt-3">
                    <button
                      onClick={handleApproveChanges}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approve Changes
                    </button>
                    <button
                      onClick={handleRejectChanges}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Reject & Rollback
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Approval Section - for pending_approval status */}
      {user?.role === 'Admin' && approval.approval_status === 'pending_approval' && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <h3 className="font-bold text-blue-800 mb-3">Admin Actions - Pending Approval</h3>
            <p className="text-sm text-blue-700 mb-3">This approval is pending your review. Please approve or reject.</p>
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Approve
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Customer Info */}
        <div className="bg-white border-2 border-amber-200 rounded-xl shadow-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-800">Customer Information</h2>
          </div>
          {customer && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-semibold">{customer.name}</span>
              </div>
              {customer.mobile && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile:</span>
                  <span className="font-semibold">{customer.mobile}</span>
                </div>
              )}
              {customer.alternate_mobile && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Alternate Mobile:</span>
                  <span className="font-semibold">{customer.alternate_mobile}</span>
                </div>
              )}
              {customer.email && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-semibold text-xs break-all">{customer.email}</span>
                </div>
              )}
              {customer.address && (
                <div className="flex flex-col gap-1">
                  <span className="text-gray-600">Address:</span>
                  <span className="font-semibold text-xs break-words">{customer.address}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="bg-white border-2 border-amber-200 rounded-xl shadow-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-800">Important Dates</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Approval Date</p>
              <p className="font-semibold">{formatDate(approval.approval_date)}</p>
            </div>
            <div>
              <p className="text-gray-500">Return Date</p>
              <p className={`font-semibold ${approval.return_date && new Date(approval.return_date) < new Date() ? 'text-red-600' : ''}`}>
                {formatDate(approval.return_date)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Created At</p>
              <p className="font-semibold">{formatDate(approval.created_at)}</p>
            </div>
            {approval.reminder_date && (
              <div>
                <p className="text-gray-500">Reminder</p>
                <p className="font-semibold text-orange-600">{formatDate(approval.reminder_date)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white border-2 border-amber-200 rounded-xl shadow-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-800">Items ({(approval.items || []).length})</h2>
          </div>
          <div className="space-y-3">
            {(approval.items || []).map((item, index) => {
              // Get item-wise rate if rate_mode is PER_ITEM
              const itemRate = (approval.rate_mode === 'per-item' || approval.rate_mode === 'PER_ITEM') && approval.item_rates 
                ? approval.item_rates[item.id]
                : null;
              
              return (
                <div key={item.id || index} className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50">
                  <div className="flex flex-col gap-3">
                    {/* Item Title */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full font-bold">#{index + 1}</span>
                        <p className="font-bold text-gray-800">{item.item_type || 'Item'}</p>
                        {item.tag_number && (
                          <span className="text-xs text-blue-600 font-semibold bg-blue-50 px-2 py-1 rounded ml-auto">Tag: {item.tag_number}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Item Photos - Below title, larger */}
                    {item.photos && item.photos.length > 0 && (
                      <div>
                        <div className="flex gap-2 flex-wrap">
                          {item.photos.map((photo, imgIdx) => (
                            <div 
                              key={imgIdx}
                              className="w-24 h-24 rounded-lg overflow-hidden cursor-pointer border-2 border-gray-200 hover:border-amber-400 transition-all hover:scale-105"
                              onClick={() => setPreviewImage(photo)}
                            >
                              <img 
                                src={photo} 
                                alt={`${item.item_type || 'Item'} ${imgIdx + 1}`} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Item Details */}
                    <div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-gray-500">Gross Wt</p>
                          <p className="font-semibold">
                            {item.gross_wt 
                              ? `${parseFloat(item.gross_wt).toFixed(3)}g`
                              : (item.weight_type === 'Fixed' 
                                ? `${item.weight_fixed || 0}g` 
                                : `${item.weight_min || 0}-${item.weight_max || 0}g`)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Less Wt</p>
                          <p className="font-semibold">{item.less_wt ? `${parseFloat(item.less_wt).toFixed(3)}g` : '0g'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Net Wt</p>
                          <p className="font-semibold text-amber-700">
                            {item.net_wt 
                              ? `${parseFloat(item.net_wt).toFixed(3)}g`
                              : (item.gross_wt 
                                ? `${parseFloat(item.gross_wt).toFixed(3)}g` 
                                : (item.weight_type === 'Fixed' ? `${item.weight_fixed || 0}g` : `${item.weight_min || 0}-${item.weight_max || 0}g`))}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                        <div>
                          <p className="text-gray-500">Purity</p>
                          <p className="font-semibold">{item.purity || '22K'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Quantity</p>
                          <p className="font-semibold">{item.quantity || 1} pc</p>
                        </div>
                      </div>
                      {item.size && (
                        <p className="text-xs text-gray-600 mt-2"><span className="font-semibold">Size:</span> {item.size}</p>
                      )}
                      
                      {/* Item-wise Rate Display - Only for PER_ITEM mode */}
                      {(approval.rate_mode === 'per-item' || approval.rate_mode === 'PER_ITEM') && itemRate && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-700 font-medium">Booked Rate ({item.purity}):</span>
                            <span className="text-xs font-bold text-green-700">
                              ₹{parseFloat(itemRate).toLocaleString('en-IN')}/g
                            </span>
                          </div>
                          {approval.gst_input_style && (
                            <p className="text-xs text-gray-600 mt-0.5">
                              {approval.gst_input_style === 'EXCL' ? 'Excl. GST' : 'Incl. GST'}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {item.public_remark && (
                        <p className="text-xs text-gray-600 mt-2 bg-white p-2 rounded border border-gray-200">{item.public_remark}</p>
                      )}
                      {item.internal_remark && (
                        <p className="text-xs text-orange-600 mt-1 italic bg-orange-50 p-2 rounded border border-orange-200">📝 Internal: {item.internal_remark}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rate & GST Info */}
        <div className="bg-white border-2 border-amber-200 rounded-xl shadow-md p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-bold text-gray-800">Rate & Tax Details</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Rate Mode</p>
              <p className="font-semibold text-blue-600">
                {(approval.rate_mode === 'per-item' || approval.rate_mode === 'PER_ITEM') ? '📊 Per-Item Rates' : '📍 Single Rate'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Rate Policy</p>
              <p className={`font-semibold ${approval.rate_policy === 'lock-today' ? 'text-green-600' : 'text-orange-600'}`}>
                {approval.rate_policy === 'lock-today' ? '🔒 Rate Locked' : 
                 approval.rate_policy === 'rate-at-delivery' ? '📅 Rate at Delivery' : 'None'}
              </p>
            </div>
            {/* Show base rate ONLY when rate_mode is SINGLE and base rate exists */}
            {(approval.rate_mode === 'single' || approval.rate_mode === 'SINGLE') && approval.base_24k_rate && (
              <div>
                <p className="text-gray-500">24K Base Rate</p>
                <p className="font-semibold text-green-700">{formatCurrency(approval.base_24k_rate)}/g</p>
              </div>
            )}
            <div>
              <p className="text-gray-500">GST</p>
              <p className="font-semibold">
                {approval.gst_applicable ? `${approval.gst_percent || 3}% (${approval.gst_input_style})` : 'N/A (Kaccha)'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Tax Scope</p>
              <p className="font-semibold">{approval.tax_scope || 'TAXABLE'}</p>
            </div>
          </div>
        </div>

        {/* Payments & Deposits - Enhanced Transaction Breakdown */}
        <div className="bg-white border-2 border-green-200 rounded-xl shadow-md p-4">
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span>💰</span> Payment & Deposits
          </h2>
          
          {(approval.advance_payments || []).length > 0 ? (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1">
                  <span>📊</span> Payment Mode Summary
                </h3>
                <div className="space-y-1.5 text-xs sm:text-sm">
                  {(() => {
                    // Calculate totals by payment mode
                    const modeTotals = {};
                    const paymentsByMode = {};
                    
                    approval.advance_payments.forEach(payment => {
                      const mode = payment.mode || payment.payment_type || 'Other';
                      const isRefund = payment.transaction_type === 'REFUND';
                      
                      if (!modeTotals[mode]) {
                        modeTotals[mode] = {
                          cashAmount: 0,
                          fineWeight: 0,
                          count: 0
                        };
                        paymentsByMode[mode] = [];
                      }
                      modeTotals[mode].count++;
                      paymentsByMode[mode].push(payment);
                      
                      if (payment.amount) {
                        const amount = parseFloat(payment.amount);
                        // Subtract refunds, add payments
                        modeTotals[mode].cashAmount += isRefund ? -amount : amount;
                      }
                      if (payment.fine_wt) {
                        modeTotals[mode].fineWeight += parseFloat(payment.fine_wt);
                      }
                      if (payment.fine_grams) {
                        modeTotals[mode].fineWeight += parseFloat(payment.fine_grams);
                      }
                    });
                    
                    // Sort modes for consistent display
                    const sortedModes = Object.keys(modeTotals).sort();
                    
                    return (
                      <>
                        {sortedModes.map(mode => {
                          const data = modeTotals[mode];
                          const isExpanded = expandedPaymentMode === mode;
                          
                          return (
                            <div key={mode}>
                              {/* Mode Summary Row - Clickable */}
                              <div className="flex justify-between items-center py-1.5 border-b border-green-100 last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-800">{mode}</span>
                                  <button
                                    onClick={() => setExpandedPaymentMode(isExpanded ? null : mode)}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer touch-target px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                                  >
                                    ({data.count} transaction{data.count > 1 ? 's' : ''})
                                  </button>
                                </div>
                                <div className="flex flex-col items-end">
                                  {data.cashAmount > 0 && (
                                    <span className="font-bold text-green-700">
                                      ₹{data.cashAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {data.fineWeight > 0 && (
                                    <span className="font-bold text-purple-700">
                                      {data.fineWeight.toFixed(3)}g
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Expanded Transaction Details for This Mode */}
                              {isExpanded && (
                                <div className="mt-2 mb-3 ml-2 space-y-2 border-l-2 border-blue-300 pl-3 animate-slide-down">
                                  {paymentsByMode[mode].map((payment, idx) => {
                                    const isRefund = payment.transaction_type === 'REFUND';
                                    return (
                                      <div key={idx} className={`border rounded-lg p-3 shadow-sm text-xs ${isRefund ? 'bg-red-50 border-red-300' : 'bg-white border-amber-200'}`}>
                                        {/* Payment Header */}
                                        <div className="flex justify-between items-start pb-2 border-b border-gray-200">
                                          <div className="flex-1">
                                            <span className={`font-semibold ${isRefund ? 'text-red-700' : 'text-gray-800'}`}>
                                              {isRefund ? '↩️ REFUND' : 'Transaction'} #{idx + 1}
                                            </span>
                                            {payment.date && (
                                              <span className="text-xs text-gray-600 block">
                                                {new Date(payment.date).toLocaleDateString('en-IN', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric'
                                                })}
                                              </span>
                                            )}
                                          </div>
                                          <span className={`font-bold text-sm ${isRefund ? 'text-red-700' : 'text-amber-700'}`}>
                                            {payment.amount ? `${isRefund ? '-' : ''}₹${parseFloat(payment.amount).toLocaleString('en-IN')}` : 
                                             payment.fine_wt ? `${parseFloat(payment.fine_wt).toFixed(3)}g` : '-'}
                                          </span>
                                        </div>
                                        
                                        {/* Payment Details */}
                                        <div className="mt-2 space-y-1 text-xs">
                                          {/* UPI Payment Details */}
                                          {payment.mode === 'UPI' && (
                                            <>
                                              {payment.upi_ref && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UPI Reference:</span>
                                                  <span className="font-medium text-gray-800">{payment.upi_ref}</span>
                                                </div>
                                              )}
                                              {payment.upi_id && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UPI ID:</span>
                                                  <span className="font-medium text-gray-800">{payment.upi_id}</span>
                                                </div>
                                              )}
                                              {payment.upi_handle && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UPI Handle:</span>
                                                  <span className="font-medium text-gray-800">{payment.upi_handle}</span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Card Payment Details */}
                                          {payment.mode === 'Card' && (
                                            <>
                                              {payment.card_last4 && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Card Last 4:</span>
                                                  <span className="font-medium text-gray-800">**** {payment.card_last4}</span>
                                                </div>
                                              )}
                                              {payment.pos_ref && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">POS Ref:</span>
                                                  <span className="font-medium text-gray-800">{payment.pos_ref}</span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {/* Bank Transfer Details */}
                                          {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && (
                                            <>
                                              {payment.utr_ref && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">UTR Reference:</span>
                                                  <span className="font-medium text-gray-800">{payment.utr_ref}</span>
                                                </div>
                                              )}
                                              {payment.bank_name && (
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Bank:</span>
                                                  <span className="font-medium text-gray-800">{payment.bank_name}</span>
                                                </div>
                                              )}
                                            </>
                                          )}
                                          
                                          {payment.reference && (
                                            <div className="flex justify-between">
                                              <span className="text-gray-600">Reference:</span>
                                              <span className="font-medium text-gray-800">{payment.reference}</span>
                                            </div>
                                          )}
                                          
                                          {/* Old Gold Details - Enhanced with full calculations */}
                                          {(payment.mode === 'Old Gold' || payment.payment_type === 'Old Gold') && (
                                            <div className="bg-purple-50 border border-purple-200 rounded p-2 mt-2">
                                              <span className="text-purple-800 font-semibold block mb-2">🪙 Old Gold Details:</span>
                                              
                                              {/* Display items if available */}
                                              {payment.items && payment.items.length > 0 ? (
                                                <div className="space-y-2">
                                                  {payment.items.map((item, itemIdx) => (
                                                    <div key={itemIdx} className="bg-white rounded p-2 border border-purple-300">
                                                      {item.item_name && (
                                                        <div className="font-medium text-purple-800 mb-1">
                                                          #{itemIdx + 1}: {item.item_name}
                                                        </div>
                                                      )}
                                                      <div className="grid grid-cols-2 gap-1">
                                                        {item.gross_wt && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Gross:</span>
                                                            <span className="font-medium">{parseFloat(item.gross_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.less_wt && parseFloat(item.less_wt) > 0 && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Less:</span>
                                                            <span className="font-medium text-red-600">{parseFloat(item.less_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.net_wt && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Net:</span>
                                                            <span className="font-medium">{parseFloat(item.net_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.purity && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Purity:</span>
                                                            <span className="font-medium">{item.purity}%</span>
                                                          </div>
                                                        )}
                                                        {item.fine_wt && (
                                                          <div className="flex justify-between col-span-2 pt-1 border-t border-purple-200 mt-1">
                                                            <span className="text-gray-700 font-semibold">Fine:</span>
                                                            <span className="font-bold text-purple-700">{parseFloat(item.fine_wt).toFixed(3)}g</span>
                                                          </div>
                                                        )}
                                                        {item.purchase_rate && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Rate:</span>
                                                            <span className="font-medium">₹{parseFloat(item.purchase_rate).toLocaleString('en-IN')}/g</span>
                                                          </div>
                                                        )}
                                                        {item.line_total && (
                                                          <div className="flex justify-between">
                                                            <span className="text-gray-600">Total:</span>
                                                            <span className="font-bold text-green-700">₹{parseFloat(item.line_total).toLocaleString('en-IN')}</span>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ))}
                                                  {/* Summary */}
                                                  <div className="bg-purple-100 rounded p-2 border border-purple-300 mt-2">
                                                    <div className="flex justify-between font-bold">
                                                      <span>Total Fine Weight:</span>
                                                      <span className="text-purple-700">{(payment.fine_grams || payment.fine_wt || 0).toFixed(3)}g</span>
                                                    </div>
                                                    <div className="flex justify-between font-bold mt-1">
                                                      <span>Total Amount:</span>
                                                      <span className="text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                /* Fallback for old data without items */
                                                <div className="text-gray-500 italic">
                                                  {payment.fine_grams || payment.fine_wt ? (
                                                    <div>
                                                      <div className="flex justify-between mb-1">
                                                        <span>Fine Weight:</span>
                                                        <span className="font-bold text-purple-700">{(payment.fine_grams || payment.fine_wt).toFixed(3)}g</span>
                                                      </div>
                                                      <div className="flex justify-between">
                                                        <span>Amount:</span>
                                                        <span className="font-bold text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    'Details not available for this transaction'
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {(payment.note || payment.remark || payment.remarks) && (
                                            <div className="mt-2 pt-2 border-t border-gray-200 bg-yellow-50 rounded p-2">
                                              <span className="text-gray-600 font-semibold">📝 Notes:</span>
                                              <p className="text-gray-800 mt-1">{payment.note || payment.remark || payment.remarks}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Grand Total */}
                        <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-green-300">
                          <span className="font-bold text-gray-900">Net Advance</span>
                          <div className="flex flex-col items-end">
                            {(() => {
                              const grandTotal = approval.advance_payments.reduce((sum, p) => {
                                const amount = parseFloat(p.amount) || 0;
                                return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
                              }, 0);
                              const totalFineWeight = approval.advance_payments.reduce((sum, p) => 
                                sum + (parseFloat(p.fine_wt) || 0) + (parseFloat(p.fine_grams) || 0), 0);
                              
                              return (
                                <>
                                  {grandTotal !== 0 && (
                                    <span className="font-bold text-green-800 text-base">
                                      ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {totalFineWeight > 0 && (
                                    <span className="font-bold text-purple-800">
                                      {totalFineWeight.toFixed(3)}g
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-500">No payments added</p>
          )}
        </div>

        {/* Payment Reminder - Show if reminder exists and is enabled */}
        {(reminder || approval.reminder_date) && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 shadow-md">
            <h2 className="text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span>💰</span> Payment Reminder Set
            </h2>
            {reminder ? (
              /* Full reminder object with all details */
              <div className="space-y-2 text-sm">
                {/* Status */}
                <div className="flex justify-between gap-2">
                  <span className="text-gray-700 font-semibold">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    reminder.status === 'resolved' ? 'bg-green-500 text-white' :
                    reminder.status === 'snoozed' ? 'bg-orange-500 text-white' :
                    reminder.status === 'overdue' ? 'bg-red-500 text-white' :
                    'bg-blue-500 text-white'
                  }`}>{reminder.status.toUpperCase()}</span>
                </div>

                {/* Reminder Reason */}
                {reminder.reason_tags && reminder.reason_tags.length > 0 && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-700 font-semibold">Reason:</span>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {reminder.reason_tags.map((tag, idx) => (
                        <span key={idx} className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Due Date & Time */}
                <div className="flex justify-between gap-2">
                  <span className="text-gray-700 font-semibold">Due:</span>
                  <span className="font-bold text-blue-700">
                    {new Date(reminder.due_at).toLocaleString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                {/* Amount Due */}
                {reminder.amount_due && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-700 font-semibold">Target Collection:</span>
                    <span className="font-bold text-green-700">₹{reminder.amount_due.toLocaleString('en-IN')}</span>
                  </div>
                )}

                {/* Channels */}
                {reminder.channels && reminder.channels.length > 0 && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-700 font-semibold">Channels:</span>
                    <div className="flex gap-1 flex-wrap justify-end">
                      {reminder.channels.map((ch, idx) => (
                        <span key={idx} className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-semibold capitalize">
                          {ch}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Auto Snooze */}
                {reminder.auto_snooze && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-700 font-semibold">Auto-Snooze:</span>
                    <span className="font-semibold text-gray-800">{reminder.auto_snooze}</span>
                  </div>
                )}

                {/* Notes */}
                {reminder.notes && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <span className="text-xs text-gray-700 font-semibold">📝 Notes:</span>
                    <p className="text-xs text-gray-800 mt-1">{reminder.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              /* Fallback: Only reminder_date available */
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-semibold">Reminder Date:</span>
                  <span className="font-bold text-blue-700">{formatDate(approval.reminder_date)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        <div className="bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-300 rounded-xl p-4 shadow-md">
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span>📊</span> Approval Summary
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700">Gross Weight:</span>
              <span className="font-bold">{totals.totalGrossWeight.toFixed(2)}g</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Net Weight:</span>
              <span className="font-bold text-amber-700">{totals.totalNetWeight.toFixed(2)}g</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Fine Weight:</span>
              <span className="font-bold text-purple-700">{totals.totalFineWeight.toFixed(3)}g</span>
            </div>
            {totals.estimatedValue > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-700">Est. Value:</span>
                <span className="font-bold">{formatCurrency(totals.estimatedValue)}</span>
              </div>
            )}
            <div className="flex justify-between text-green-700">
              <span>Advance Paid:</span>
              <span className="font-bold">{formatCurrency(totals.totalAdvance)}</span>
            </div>
            {totals.estimatedValue > 0 && (
              <div className="flex justify-between pt-2 border-t-2 border-amber-400">
                <span className="font-bold text-gray-800">Balance:</span>
                <span className="font-bold text-xl text-red-700">
                  {formatCurrency(totals.estimatedValue - totals.totalAdvance)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Signatures */}
        {(approval.management_signature || approval.customer_signature) && (
          <div className="bg-white border-2 border-purple-200 rounded-xl shadow-md p-4">
            <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span>✍️</span> Signatures
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {approval.management_signature && (
                <div className="bg-purple-50 rounded-lg p-3 border-2 border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-semibold">Management</p>
                  <img 
                    src={approval.management_signature} 
                    alt="Management Signature" 
                    className="w-full h-20 object-contain bg-white rounded border cursor-pointer"
                    onClick={() => setPreviewImage(approval.management_signature)}
                  />
                  {approval.management_signature_name && (
                    <p className="text-center text-sm font-bold text-purple-700 mt-2">
                      {approval.management_signature_name}
                    </p>
                  )}
                </div>
              )}
              {approval.customer_signature && (
                <div className="bg-purple-50 rounded-lg p-3 border-2 border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-semibold">Customer</p>
                  <img 
                    src={approval.customer_signature} 
                    alt="Customer Signature" 
                    className="w-full h-20 object-contain bg-white rounded border cursor-pointer"
                    onClick={() => setPreviewImage(approval.customer_signature)}
                  />
                  {customer && (
                    <p className="text-center text-sm font-bold text-purple-700 mt-2">
                      {customer.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revision History */}
        {approval.revision_history && approval.revision_history.length > 0 && (
          <div className="bg-white border-2 border-indigo-200 rounded-xl shadow-md p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-800">Revision History</h2>
              </div>
              <span className="text-sm text-gray-500 bg-indigo-100 px-3 py-1 rounded-full font-semibold">
                {approval.revision_history.length} revision(s)
              </span>
            </div>
            <div className="space-y-4">
              {approval.revision_history.map((revision, idx) => (
                <RevisionTimelineCard
                  key={idx}
                  revision={revision}
                  revisionNumber={revision.revision_number || (approval.revision_history.length - idx)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]">
            <button 
              className="absolute -top-10 right-0 text-white p-2"
              onClick={() => setPreviewImage(null)}
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={previewImage} 
              alt="Preview" 
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Approval?</h3>
            <p className="text-gray-600 mb-4">
              This will permanently delete this approval and all associated data. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Reject Approval</h3>
            <p className="text-gray-600 mb-3 text-sm">Please provide a reason for rejection:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Format Selector Modal */}
      <PrintFormatSelector
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onSelectFormat={handlePrintFormat}
        formats="approval"
        hasItemReceipts={getOldGoldAndMetalCreditItems().length > 0}
      />

      {/* Print Components - Only render the selected one */}
      {approval && printFormat === 'customer' && (
        <ApprovalPrintA4Customer approval={approval} customer={customer} termsAndConditions={termsAndConditions} />
      )}
      {approval && printFormat === 'internal' && (
        <ApprovalPrintA4Internal approval={approval} customer={customer} reminder={reminder} termsAndConditions={termsAndConditions} />
      )}
      {approval && printFormat === 'thermal-customer' && (
        <ApprovalPrintThermalCustomer approval={approval} customer={customer} termsAndConditions={termsAndConditions} />
      )}
      {approval && printFormat === 'thermal-internal' && (
        <ApprovalPrintThermalInternal approval={approval} customer={customer} reminder={reminder} termsAndConditions={termsAndConditions} />
      )}

      {/* Item Receipt Selector Modal */}
      <ItemReceiptSelector
        isOpen={showItemReceiptSelector}
        onClose={() => setShowItemReceiptSelector(false)}
        items={getOldGoldAndMetalCreditItems()}
        onPrintSelected={handlePrintSelectedItems}
      />

      {/* Item Receipt Print Components (Hidden from screen, only for printing) */}
      <div className="item-receipts-container">
        {getOldGoldAndMetalCreditItems().map((item, index) => (
          <ItemReceiptPrint
            key={index}
            item={item}
            itemIndex={index}
            customer={customer}
            moduleRef={`Approval: ${approval?.approval_id}`}
            transactionDate={item.payment_date || approval?.created_at}
            transactionId={item.payment_id}
          />
        ))}
      </div>
    </div>
  );
};

export default ApprovalDetail;
